import React, { type ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import ConfirmModal, { ConfirmModalActionPair } from 'atom-components/confirm-modal';
import { pluralise } from 'helpers/string.helpers';
import {
  type AttentionReasonFormValue,
  type MarkResponseFormValues
} from 'pages/marking/subpages/mark-response-page/mark-response-form/mark-response-form.schema';
import { type BaseTag } from 'services/assist-api/entities/tag/tags';

/**
 * Returns the attention reasons that have been checked since the form was last saved.
 */
export function newlyCheckedAttentionReasons(
  savedAttentionReasons: AttentionReasonFormValue[],
  attentionReasons: AttentionReasonFormValue[]
): AttentionReasonFormValue[] {
  return attentionReasons.filter((ar) =>
    savedAttentionReasons.find((sar) => sar.id === ar.id && !sar.checked && ar.checked)
  );
}

/**
 * Checks if the form should ask if "Flag for attention" should be set.
 */
export function shouldAskToFlagForAttention(
  canAdministrateMark: boolean,
  defaultValues: MarkResponseFormValues,
  formValues: MarkResponseFormValues
): boolean {
  const newAttentionReasons = newlyCheckedAttentionReasons(
    defaultValues.attentionReasons,
    formValues.attentionReasons || []
  );
  return (
    !formValues.hasAskedToFlagForAttention &&
    !canAdministrateMark &&
    !formValues.flagForAttention &&
    (newAttentionReasons.length > 0 || defaultValues.comment !== formValues.comment)
  );
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  defaultValues: MarkResponseFormValues;
  attentionReasons: BaseTag[];
};

export default function ConfirmFlagForAttentionModal({
  isOpen,
  onClose,
  onConfirm,
  defaultValues,
  attentionReasons
}: Props): ReactElement {
  const { watch } = useFormContext();

  const watchedAttentionReasons = watch('attentionReasons');

  const newAttentionReasons = newlyCheckedAttentionReasons(
    defaultValues.attentionReasons,
    watchedAttentionReasons
  );

  const comment = watch('comment');

  return (
    <ConfirmModal
      isOpen={isOpen}
      title="Confirm Flag for Attention"
      handleClickCancel={onClose}
      handleClickOK={onConfirm}
      actionPair={ConfirmModalActionPair.Custom}
      buttonTitleOK="Confirm Flag for Attention"
      buttonTitleCancel="No Review Needed"
    >
      {newAttentionReasons.length > 0 && (
        <>
          <p>
            You have specified the following {newAttentionReasons.length} new{' '}
            {pluralise('reason', newAttentionReasons.length)} for marking administration review:
          </p>
          {newAttentionReasons.map((attentionReason) => (
            <ul>
              <li>{attentionReasons.find((ar) => ar.id === attentionReason.id)?.name}</li>
            </ul>
          ))}
        </>
      )}
      {defaultValues.comment !== comment && <p>You have made changes to the comment.</p>}
      <p>Does this response need further review by a marking administrator?</p>
    </ConfirmModal>
  );
}
